import {Button, DatePicker, Dropdown, Select, Tag} from 'antd';
import moment from 'moment';
import dayjs, {Dayjs} from 'dayjs';
import React, {useMemo, useState} from 'react';
import {LoadingOutlined, PlusOutlined} from '@ant-design/icons';

import {brandConfig, CONSTANTS, getCampaignTypes} from '../../utils';
import StatusesMap from '../audienceStats/statusesMap';
import {ProspectsTableColumnKeys} from '../../../../types/src';

import './prospectsFilter.scss';

const {Option} = Select;
const {RangePicker} = DatePicker;

const dateOptions = [
  {
    label: 'Lifetime',
    value: 'lifetime',
  },
  {
    label: 'Last 30 days vs last previous 30 days',
    value: 'last_30_days_vs_prev_30_days',
    className: 'prospects-page__last30vsprev30-option',
  },
  {
    label: 'Last 7 days',
    value: 'last_7_days',
  },
  {
    label: 'Last 30 days',
    value: 'last_30_days',
  },
  {
    label: 'Last 3 Months',
    value: 'last_3_months',
  },
  {
    label: 'Last 6 months',
    value: 'last_6_months',
  },
  {
    label: 'Last 12 months',
    value: 'last_12_months',
  },
  {
    label: 'Custom',
    value: 'custom',
  },
];

const CAMPAIGN_TYPES = [
  CONSTANTS.campaignTypes.acquisition,
  CONSTANTS.campaignTypes.acquisition_csv,
  CONSTANTS.campaignTypes.acquisition_via_import,
  CONSTANTS.campaignTypes.retention,
  CONSTANTS.campaignTypes.retention_csv,
  CONSTANTS.campaignTypes.retention_actions,
  CONSTANTS.campaignTypes.retention_tags,
  CONSTANTS.campaignTypes.acquisition_inmail,
];

enum FilterType {
  User = 'user',
  Campaign = 'campaign',
  CampaignType = 'campaign_type',
}

const possibleFilters = [
  {label: 'Users', key: FilterType.User},
  {label: 'Campaigns', key: FilterType.Campaign},
  {label: 'Type', key: FilterType.CampaignType},
];

const getActiveFilters = (filters) => {
  const initialFilters = [];
  if (filters?.user?.length) initialFilters.push(FilterType.User);
  if (filters?.campaign?.length) initialFilters.push(FilterType.Campaign);
  if (filters?.campaign_type?.length) initialFilters.push(FilterType.CampaignType);
  return initialFilters;
}

const getSearchableFiltersColumns = () => {
  return CONSTANTS.prospectsTableColumns.filter(
    v => ![
      ProspectsTableColumnKeys.User,
      ProspectsTableColumnKeys.Campaign]
      .includes(v.value)
  );
}

export const ProspectsFilter = ({
  prospects,
  users,
  campaigns,
  setFilters,
  filters,
  totalProspects,
  totalProspectsLoading,
  prospectsLoading,
  selectedRows,
}: any) => {
  const [showCustom, setShowCustom] = useState(false);
  const [showDatesSelect, setShowDatesSelect] = useState(false);
  const [shownFilters, setShownFilters] = useState(getActiveFilters(filters));
  const filterMenu = useMemo(() => possibleFilters.filter(f => !shownFilters.includes(f.key)), [shownFilters]);
  const searchableFiltersColumns = getSearchableFiltersColumns();
  const searchableFilters = useMemo(() => {
    const searchFilters = searchableFiltersColumns.map(({label, value}) => {
      if (filters[value]?.[0]) {
        return {label: `${label}: ${filters[value]?.[0]}`, key: value}
      }
      return null;
    });
    return searchFilters.filter(v => v);
  }, [filters, searchableFiltersColumns]);
  const isLoading = totalProspectsLoading || prospectsLoading;

  const filterCampaigns = filters?.user?.length
    ? campaigns.filter(c => filters.user.includes(c.selectedUser?.id))
    : campaigns;

  const handleUsersChange = user => setFilters({...filters, user, start: 0});
  const handleCampaignsChange = campaign => setFilters({...filters, campaign, start: 0});
  const handleCampaignsChangeType = campaign_type => setFilters({...filters, campaign_type, start: 0});
  const onAddFilter = ({key}) => setShownFilters(v => [...v, key]);

  const lastTwoMonthRange: [Dayjs, Dayjs] = [dayjs().subtract(1, 'months'), dayjs()];
  const disableFutureDates = currentDate => currentDate && currentDate >= moment().endOf('day');

  const handleDateChange = date => {
    if (date === 'custom') {
      setShowCustom(true);
      return;
    }
    setShowCustom(false);
    setFilters({
      ...filters,
      date,
      date_range_start: '',
      date_range_end: '',
      start: 0,
    });
  };

  const customDates = arg => {
    setShowCustom(false);
    setShowDatesSelect(false);
    let [start, end] = arg;
    start = start.format('YYYY-MM-DD');
    end = end.format('YYYY-MM-DD');
    setFilters({
      ...filters,
      date: `${start} - ${end}`,
      date_range_start: start,
      date_range_end: end,
      start: 0,
    });
  };

  const onDeleteFilter = (key: string) => {
    const newFilters = {...filters};
    delete newFilters[key];
    setFilters(newFilters);
  }

  const getStatusLabel = (status) => {
    const allStatuses = StatusesMap.getAllStatuses();
    const statusData = allStatuses.get(status);
    return statusData.label ?? status;
  };

  const renderFilters = (filter) => {
    switch (filter) {
      case FilterType.User:
        return (
          <Select
            key={FilterType.User}
            loading={isLoading}
            mode="multiple"
            placeholder="Select users"
            onChange={handleUsersChange}
            value={users.length > 0 ? filters?.user : undefined}
            className="prospects-page__filter-input"
            filterOption={(input, option) =>
              (option.props.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {users.map(user => (
              <Option key={user.id} value={user.id}>
                {user.first_name ? `${user.first_name} ${user.last_name}` : user.email}
              </Option>
            ))}
          </Select>
        )
      case FilterType.Campaign:
        return (
          <Select
            key={FilterType.Campaign}
            loading={isLoading}
            mode="multiple"
            placeholder="Select campaign"
            onChange={handleCampaignsChange}
            value={filterCampaigns.length > 0 ? filters?.campaign : undefined}
            filterOption={(input, option) =>
              (option.props.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            className="prospects-page__filter-input"
          >
            {filterCampaigns.map((c: any) => (
              <Option key={c.id}>{c.name}</Option>
            ))}
          </Select>
        )
      case FilterType.CampaignType:
        return (
          <Select
            key={FilterType.CampaignType}
            mode="multiple"
            placeholder="Select type"
            onChange={handleCampaignsChangeType}
            value={filters?.campaign_type}
            filterOption={(input, option) =>
              (option.props.children as string).toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            className="prospects-page__filter-input"
          >
            {CAMPAIGN_TYPES.map(type => (
              <Option key={type} className="ghp">
                {getCampaignTypes(type).value}
              </Option>
            ))}
          </Select>
        )
    }
  }

  return (
    <div className="prospects-page__filters">
      <div>
        <RangePicker
          className="custom-range__datepicker"
          open={showCustom}
          onChange={customDates}
          defaultValue={lastTwoMonthRange}
          disabledDate={disableFutureDates}
        />
        <Select
          open={showDatesSelect}
          value={filters?.date}
          className="prospects-page__filter-input"
          onDropdownVisibleChange={open => setShowDatesSelect(open)}
          onChange={handleDateChange}
        >
          {dateOptions.map(item => (
            <Option className={item.className} key={item.value}>
              {item.label}
            </Option>
          ))}
        </Select>
      </div>

      {shownFilters.map(renderFilters)}

      {filters?.status && (
        <Tag
          key="status"
          closable={true}
          color={brandConfig.primaryColor}
          onClose={() => onDeleteFilter('status')}
        >
          {`Status: ${getStatusLabel(filters.status)}`}
        </Tag>
      )}

      {searchableFilters.map(({label, key}) => (
        <Tag
          key={key}
          closable={true}
          color={brandConfig.primaryColor}
          onClose={() => onDeleteFilter(key)}
        >
          {label}
        </Tag>
      ))}

      <Dropdown
        menu={{items: filterMenu, onClick: onAddFilter}}
        trigger={['click']}
        placement="bottom"
        disabled={filterMenu.length === 0}
      >
        <Button type="link" icon={<PlusOutlined />}>
          Add filter
        </Button>
      </Dropdown>

      <span className="prospects-page__selected-count">
        {isLoading ? (
          <span>
            <LoadingOutlined /> loading...
          </span>
        ) : (
          `Showing ${prospects?.length} of ${totalProspects} (${selectedRows?.length} Selected)`
        )}
      </span>
    </div>
  );
};
